import React, { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProfile } from '../contexts/ProfileContext'
import { useTenant } from '../contexts/TenantContext'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { isGlobalSuperadmin } from '../lib/authz'
import { useEmployeeSelf } from '../hooks/useEmployeeSelf'
import { ArrowLeft, Shield, RefreshCw, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'
import { CalmLoadingShell } from './system/CalmLoadingShell'

interface ProtectedRouteProps {
  children: ReactNode
  /**
   * 'admin'    — owner or admin only (members are redirected to /workspace)
   * 'internal' — owner, admin, or member (employees are redirected to /portal)
   * 'portal'   — anyone with an ACTIVE EMPLOYEE RECORD of their own in this
   *              tenant, whatever their administrative role. Not a role check:
   *              an owner who also works here has an employee record and an
   *              employee experience, and gating that on `role = 'employee'`
   *              made their own leave and documents unreachable.
   */
  requiredRole?: 'admin' | 'portal' | 'internal'
  fallback?: ReactNode
}

export default function ProtectedRoute({
  children,
  requiredRole,
  fallback
}: ProtectedRouteProps) {
  const { authState, user, signOut, retry: retryAuth } = useAuth()
  const { profileState, profile, retry: retryProfile } = useProfile()
  const { tenantState, retry: retryTenant, tenantError } = useTenant()
  // Only the portal gate needs this; hooks cannot be called conditionally, so
  // the flag turns the query off rather than the call site.
  const employeeSelf = useEmployeeSelf(requiredRole === 'portal')
  // Superadmin comes from profiles.global_role — the only server-enforced
  // source (a BEFORE UPDATE trigger blocks self-modification).
  const isSuperadmin = isGlobalSuperadmin(profile)
  const navigate = useNavigate()
  const location = useLocation()

  // ── Auto-recovery: if tenant failed but profile already has a tenant_id,
  // retry once automatically.  This bridges the race where a transient auth-
  // endpoint failure caused the tenant load to fail while the DB query
  // (used by ProfileProvider) already succeeded and proved the tenant exists.
  const autoRetryDoneRef = useRef(false)
  useEffect(() => {
    if (
      tenantState === 'error' &&
      profile?.tenant_id &&
      !autoRetryDoneRef.current
    ) {
      autoRetryDoneRef.current = true
      retryTenant()
    }
    // Reset the flag when the workspace loads successfully so a future
    // real error can still trigger a single auto-retry.
    if (tenantState === 'selected') {
      autoRetryDoneRef.current = false
    }
  }, [tenantState, profile?.tenant_id, retryTenant])

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 1 — Initializing (auth + profile + tenant all in one shell)
  //
  // Auth, profile, and tenant load in parallel but gate sequentially here.
  // Collapsing them avoids 2–3 successive full-screen shell transitions on
  // first load — the user sees a single loading shell until everything is
  // ready, then the page renders.
  // ─────────────────────────────────────────────────────────────────────────
  const isInitializing =
    authState === 'loading' ||
    (authState === 'signedIn' && (profileState === 'idle' || profileState === 'loading')) ||
    (profileState === 'loaded' && (tenantState === 'idle' || tenantState === 'loading'))

  if (isInitializing) {
    return <CalmLoadingShell context="auth" />
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 2 — Auth error
  // ─────────────────────────────────────────────────────────────────────────
  if (authState === 'error') {
    return (
      <SystemErrorScreen
        title="Authentication Error"
        message="Failed to initialise authentication. Please try again."
        onRetry={retryAuth}
        onSignIn={() => navigate(requiredRole === 'portal' ? '/portal/sign-in' : '/admin/sign-in', { replace: true })}
      />
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 3 — Not signed in → redirect
  // ─────────────────────────────────────────────────────────────────────────
  if (authState === 'signedOut' || !user) {
    const loginPath = requiredRole === 'portal' ? '/portal/sign-in' : '/admin/sign-in'
    const currentPath = location.pathname + location.search
    return <Navigate to={`${loginPath}?next=${encodeURIComponent(currentPath)}`} replace />
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 4 — Profile missing (workspace being created)
  // ─────────────────────────────────────────────────────────────────────────
  if (profileState === 'missing') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full border-2 border-border border-t-[#1F2A44] animate-spin mx-auto mb-5" />
          <h1 className="text-[17px] font-semibold text-foreground mb-1.5">Creating Your Workspace</h1>
          <p className="text-sm text-muted-foreground">Setting up your account…</p>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 5 — Profile error
  // ─────────────────────────────────────────────────────────────────────────
  if (profileState === 'error') {
    return (
      <SystemErrorScreen
        title="Profile Error"
        message="Failed to load your profile. Please try again."
        onRetry={retryProfile}
        onSignIn={() => navigate(requiredRole === 'portal' ? '/portal/sign-in' : '/admin/sign-in', { replace: true })}
      />
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 9 — No tenant found
  // ─────────────────────────────────────────────────────────────────────────
  if (tenantState === 'none') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[rgba(31,42,68,0.06)] rounded-xl flex items-center justify-center mx-auto mb-5">
            <Shield className="w-6 h-6 text-brand" />
          </div>
          <h1 className="text-[17px] font-semibold text-foreground mb-1.5">No Workspace Found</h1>
          <p className="text-sm text-muted-foreground mb-5">
            Your account isn't linked to a workspace. If you were invited,
            open your invitation link again — or contact your administrator.
          </p>
          <button
            onClick={async () => {
              await signOut().catch(() => {})
              navigate(requiredRole === 'portal' ? '/portal/sign-in' : '/admin/sign-in', { replace: true })
            }}
            className="w-full bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 10 — Tenant error
  //
  // When the auto-recovery effect above detects profile.tenant_id it calls
  // retryTenant(), which moves state back to 'loading'.  So by the time we
  // reach this branch the auto-retry has either already fired (and is now
  // in 'loading') or the profile genuinely has no tenant_id (real error).
  // ─────────────────────────────────────────────────────────────────────────
  if (tenantState === 'error') {
    const isNetwork =
      tenantError?.toLowerCase().includes('network') ||
      tenantError?.toLowerCase().includes('unavailable') ||
      tenantError?.toLowerCase().includes('offline')

    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-5 ${
            isNetwork ? 'bg-amber-50' : 'bg-[#FEF2F2]'
          }`}>
            {isNetwork
              ? <WifiOff className="w-6 h-6 text-amber-600" />
              : <Shield className="w-6 h-6 text-rose-600" />}
          </div>

          <h1 className="text-[17px] font-semibold text-foreground mb-1.5">
            {isNetwork ? 'Connection Issue' : 'Workspace Error'}
          </h1>

          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            {isNetwork
              ? 'Unable to reach the workspace. Check your connection and tap Retry.'
              : (tenantError || 'Failed to load workspace. Please try again.')}
          </p>

          <div className="space-y-2.5">
            <button
              onClick={retryTenant}
              className="w-full flex items-center justify-center gap-2 bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
            <button
              onClick={() => navigate(
                requiredRole === 'portal' ? '/portal/sign-in' : '/admin/sign-in',
                { replace: true }
              )}
              className="w-full bg-panel text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-border transition-colors"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Gate 11 — Tenant selected — check role-based access
  // ─────────────────────────────────────────────────────────────────────────
  if (tenantState === 'selected') {
    if (requiredRole === 'admin') {
      const isOwnerOrAdmin = profile?.role === 'owner' || profile?.role === 'admin'
      const hasAccess = isSuperadmin || isOwnerOrAdmin

      if (!hasAccess) {
        if (profile?.role === 'member') {
          return <Navigate to="/workspace" replace />
        }
        if (profile?.role === 'employee') {
          return <Navigate to="/portal" replace />
        }
        return fallback || (
          <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-5">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <h1 className="text-[17px] font-semibold text-foreground mb-1.5">Access Denied</h1>
              <p className="text-sm text-muted-foreground mb-5">
                This area is restricted to administrators.
              </p>
              <button
                onClick={() => window.history.back()}
                className="w-full flex items-center justify-center gap-2 bg-panel text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-border transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Go Back
              </button>
            </div>
          </div>
        )
      }
    }

    if (requiredRole === 'internal') {
      const isInternal =
        isSuperadmin ||
        profile?.role === 'owner' ||
        profile?.role === 'admin' ||
        profile?.role === 'member'

      if (!isInternal) {
        return <Navigate to="/portal" replace />
      }
    }

    // ───────────────────────────────────────────────────────────────────────
    // The employee portal.
    //
    // Access follows the employee record, not the administrative role. Three
    // outcomes, and the difference between them matters to the person on the
    // other side of the screen:
    //
    //   active   — a live employee record here. Let them in, whoever they are.
    //   inactive — archived, offboarded or erased. A historical record, not a
    //              current employee experience. Refused, and said plainly.
    //   none     — no employee record at all. Also refused, but for a
    //              different reason and with a different way out.
    //
    // Nothing here grants anything. Every leave and document RPC re-derives
    // the caller's authority from auth.uid(); an owner standing in their own
    // employee view is, to the database, exactly the employee they are.
    // ───────────────────────────────────────────────────────────────────────
    if (requiredRole === 'portal') {
      if (employeeSelf.state === 'loading') {
        return <CalmLoadingShell />
      }

      if (employeeSelf.state === 'error') {
        return (
          <SystemErrorScreen
            title="We could not open your employee view"
            message="Your records are safe. The connection may have dropped."
            onRetry={employeeSelf.refetch}
            onSignIn={() => navigate('/portal/sign-in', { replace: true })}
          />
        )
      }

      if (employeeSelf.state !== 'active') {
        return <NoEmployeeRecordScreen
          reason={employeeSelf.state === 'inactive' ? 'inactive' : 'none'}
          adminHome={internalHomeFor(profile?.role)}
          onGoAdmin={(to) => navigate(to, { replace: true })}
          onSignOut={async () => { await signOut().catch(() => {}); navigate('/portal/sign-in', { replace: true }) }}
        />
      }
    }

    return <>{children}</>
  }

  // Fallback (should never reach)
  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center">
      <div className="w-8 h-8 rounded-full border-2 border-border border-t-[#1F2A44] animate-spin" />
    </div>
  )
}

// ─── Shared error screen ──────────────────────────────────────────────────────

function SystemErrorScreen({
  title,
  message,
  onRetry,
  onSignIn,
}: {
  title: string
  message: string
  onRetry?: () => void
  onSignIn: () => void
}) {
  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 bg-[#FEF2F2] rounded-xl flex items-center justify-center mx-auto mb-5">
          <Shield className="w-6 h-6 text-rose-600" />
        </div>
        <h1 className="text-[17px] font-semibold text-foreground mb-1.5">{title}</h1>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{message}</p>
        <div className="space-y-2.5">
          {onRetry && (
            <button
              onClick={onRetry}
              className="w-full flex items-center justify-center gap-2 bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
          <button
            onClick={onSignIn}
            className="w-full bg-panel text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-border transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Where an internal user's own workspace is.
 *
 * Used only to offer a way out of the portal denial screen — never to decide
 * whether someone may enter it. Returns null for a portal-only account, who
 * has no administrative area to be sent to.
 */
function internalHomeFor(role: string | null | undefined): { to: string; label: string } | null {
  if (role === 'owner' || role === 'admin') return { to: '/dashboard', label: 'Go to the admin workspace' }
  if (role === 'member') return { to: '/workspace', label: 'Go to your workspace' }
  return null
}

/**
 * "You cannot open an employee view here."
 *
 * Two genuinely different situations, and conflating them would leave the
 * reader unable to act:
 *
 *   none      Nobody has set this account up as an employee. Common and
 *             fixable — an administrator adds the record.
 *   inactive  There IS a record, but it is archived, offboarded or erased.
 *             Nothing is wrong; that person is no longer employed here, and
 *             the historical record is not a live portal.
 *
 * Both offer a route onward: back to administration if the account has one,
 * and sign-out if it does not.
 */
function NoEmployeeRecordScreen({
  reason,
  adminHome,
  onGoAdmin,
  onSignOut,
}: {
  reason: 'none' | 'inactive'
  adminHome: { to: string; label: string } | null
  onGoAdmin: (to: string) => void
  onSignOut: () => void
}) {
  const copy = reason === 'inactive'
    ? {
        title: 'This employee record is no longer active',
        body:
          'The employee record linked to your account has been archived or closed, so there is no ' +
          'current employee view to open. Your record is kept, but it is not a live portal.',
      }
    : {
        title: 'You do not have an employee record here',
        body:
          'The employee portal shows your own leave, documents and details, and your account is not ' +
          'set up as an employee in this workspace. An administrator can add you.',
      }

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-border shadow-sm p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 bg-[rgba(31,42,68,0.06)] rounded-xl flex items-center justify-center mx-auto mb-5">
          <Shield className="w-6 h-6 text-brand" />
        </div>
        <h1 className="text-[17px] font-semibold text-foreground mb-1.5">{copy.title}</h1>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{copy.body}</p>
        <div className="space-y-2.5">
          {adminHome && (
            <button
              onClick={() => onGoAdmin(adminHome.to)}
              className="w-full flex items-center justify-center gap-2 bg-brand text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {adminHome.label}
            </button>
          )}
          <button
            onClick={onSignOut}
            className="w-full bg-panel text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-border transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
